// Bind·Charge conduction: a wire equalizes, and it does it now.
//
// Built 2026-08-07 alongside the mechanic, on the room-decay tick with a
// `chargeEquilibrationRate` of 0.5, mirroring Bind·Energy. Moved here
// 2026-08-08, to settle and to total equalization, for a reason worth writing
// down because it also says what a rate constant is *for*.
//
// **The rate was buying nothing.** A reciprocal Bind edge moves each side by
// `rate` of the gap *toward the other*, so at 0.5 both sides meet exactly at
// the mean in a single tick -- a two-locus link was already instantaneous, and
// the 3-second cadence was pure latency between the cast and its consequence
// rather than a curve anyone could watch or exploit. Where a curve did exist
// (three or more loci chained, a node with two edges summing two half-gap
// deltas) it lurched: 8/0/0 went to 4/4/0, then 4/2/2, then 3/3/2, converging
// on the right answer along a path nobody could plan against. Neither is a
// time constant in any useful sense.
//
// **And the physics says immediate.** Thermal diffusion through a solid is
// genuinely slow, seconds to minutes, which is why Bind·Energy keeps its rate
// and stays in `thermal.ts` on the tick. Charge redistribution through a
// conductor is an RC time constant measured in nanoseconds -- instantaneous at
// any scale a player perceives. The two mechanics look symmetric and are not,
// and pretending otherwise cost the faster one its legibility. The tax is
// unaffected: the energy dissipated redistributing charge through a resistor
// is independent of the resistance (the standard capacitor-charging result),
// so making conduction instant delivers exactly the same heat, in one lump
// instead of a trickle.
//
// What the change buys, beyond removing a clock the design review argues no
// puzzle game should have: **exact division.** Bind a Font holding 8 to an
// empty locus and both read exactly 4; chain three and each reads 8/3. That is
// a precise, plannable quantity the Precise/Standard/Unbound ladder cannot
// express, and it exists only at total equalization -- at any rate below 1 you
// land on whatever the curve happened to reach.
//
// Two structural consequences, both good:
//
// - **Idempotence.** An equalized component has nothing left to move, so
//   running this twice is the same as running it once. That is what makes it
//   safe as a settle system (settle runs on every mutation), and it retires an
//   entire bug class: the mutually-declared pair that oscillated forever under
//   a rate (see `12_Mechanics_Charge.md`) is simply not expressible here --
//   equalizing an already-equal component is a no-op regardless of how many
//   times the link is declared or in which direction.
// - **Components, not pairs.** Equalizing a *connected component* rather than
//   each edge in turn is what makes a chain resolve in one step and makes the
//   result independent of object order. A wire is not a pair of endpoints; it
//   is one locus wearing several bodies.
import { WorldState } from './state';
import { countedDelta } from './physics';
import { RoomDef, ObjectDef } from './types';
import { SimEvents } from './events';
import { giveThermal } from './player';
import { chargeNetwork } from './queries';

/** One locus in a component: either the caster or an object, with the getter
 * and setter for whichever field actually holds its Charge. The player's lives
 * in `player.charge` and an object's in `properties.charge`, which is the only
 * reason this indirection exists. */
interface ChargeNode {
  key: string;
  /** Whether this locus is the caster passing through rather than part of the
   * network's own constitution. Read by `equalizedValues` for exactly one
   * question -- whether the network has a store of its own -- because that has
   * to be a property of the network, not of who happens to be touching it. See
   * the degeneracy note there. */
  isCaster?: boolean;
  get(): number;
  set(value: number): void;
  /** Electrical capacitance -- how much of the network's charge this locus
   * actually stores (`ObjectDef.capacity`, absent meaning 0). A capacity-0
   * locus sits at the network's potential holding nothing, which is what lets
   * a wire or a conductive floor carry a source's reading to the far end
   * without draining it. Charge found sitting on one is not an error: it
   * redistributes to whatever in the component can hold it, which is what a
   * real conductor does. */
  capacity: number;
  /** The most this locus can hold. `maxProperties.charge` where declared, and
   * otherwise unbounded -- objects have no implicit ceiling, matching what the
   * per-edge clamp in `thermal.ts` always did. Never below what the locus is
   * *currently* holding: a reservoir that overshot its own maximum (unclamped
   * Project overfill is a known and deliberate hole, see the design review's
   * Phase 7) must still be able to give that excess away rather than have this
   * function treat the component's total as unplaceable. */
  ceiling: number;
}

/** The caster's capacity is 1, and deliberately not a `PhysicsConfig` dial: it
 * is the unit the model is defined against rather than a number anything
 * balances. With C = 1, `V = Q / C` means the caster's potential *is* their
 * charge -- which is what lets a floor hazard read a potential difference
 * against them with no conversion, and what caps what they can carry away from
 * a network at V rather than at some second number. Tuning it would not make
 * the game easier or harder; it would make the arithmetic incoherent. */
const PLAYER_CAPACITY = 1;

function playerNode(world: WorldState): ChargeNode {
  return {
    key: 'player',
    isCaster: true,
    get: () => world.player.charge,
    set: (value) => {
      world.player.charge = value;
    },
    capacity: PLAYER_CAPACITY,
    ceiling: Math.max(world.physics.player.maxCharge, world.player.charge),
  };
}

function objectNode(obj: ObjectDef): ChargeNode {
  const current = obj.properties!.charge!;
  return {
    key: obj.id,
    get: () => obj.properties!.charge!,
    set: (value) => {
      obj.properties!.charge = value;
    },
    capacity: obj.capacity ?? 0,
    ceiling: Math.max(obj.maxProperties?.charge ?? Infinity, current),
  };
}

/** Every connected component of the room's Bind·Charge graph that has at least
 * two loci in it. Nodes are objects carrying a `properties.charge` value, plus
 * the caster where a link names them; edges are `chargeBind` declarations,
 * which are undirected for conduction purposes -- a wire does not care which
 * end was cast at, and neither does this.
 *
 * A link whose partner has no Charge property at all contributes no edge (the
 * same guard the per-edge version had): there is nothing there to equalize
 * with, and inventing a zero would let a wire drain a locus into an object that
 * does not participate in Charge at all. */
function chargeComponents(world: WorldState, room: RoomDef): ChargeNode[][] {
  const nodeFor = (locus: { kind: 'object'; obj: ObjectDef } | { kind: 'caster' }): ChargeNode =>
    locus.kind === 'caster' ? playerNode(world) : objectNode(locus.obj);

  // Seeded from the wires, because a wire is the only thing that *moves*
  // charge: adjacency and co-location conduct a reading (see networkPotential)
  // but redistribute nothing on their own, exactly as they never did. What the
  // shared walk buys is that once a wire is involved, this equalizes across
  // precisely the network `isConduitLive`/`isConduitCharged` read -- one
  // definition of connectivity, so the writer and the readers cannot drift.
  const seen = new Set<string>();
  const components: ChargeNode[][] = [];
  for (const seed of room.objects) {
    if (!seed.chargeBind || seed.properties?.charge === undefined || seen.has(seed.id)) continue;
    const loci = chargeNetwork(room, seed, world.physics, { charge: world.player.charge });
    // Only loci that can actually carry a Charge value participate: a wire to
    // something with no `properties.charge` at all has nothing to equalize
    // with, and inventing a zero would let it drain into an object that does
    // not take part in Charge (the same guard the per-edge version had).
    const members = loci.filter((l) => l.kind === 'caster' || l.obj.properties?.charge !== undefined);
    for (const l of members) if (l.kind === 'object') seen.add(l.obj.id);
    if (members.length > 1) components.push(members.map(nodeFor));
  }
  return components;
}

/** The value each locus in a component settles on.
 *
 * Joined conductors share a **potential**, not a charge: `V = SumQ / SumC`, and
 * each locus then holds `C_i * V` (see `04_Physics_Engine.md`'s Conservation of
 * Charge section). Equal division -- what this did until 2026-08-08 -- is only
 * the special case where every locus has the same capacitance, which is what it
 * silently assumed. This is a strict generalization: with every `C_i` at 1,
 * `SumC` is the node count, `C_i * V` is the mean, and the arithmetic is
 * unchanged.
 *
 * The water-filling around it keeps its shape: any locus whose ceiling sits
 * below its share is pinned there and the remainder redistributed among the
 * rest, repeated until nothing is over its ceiling. It terminates because each
 * round either finishes or removes at least one locus from the pool, and it
 * conserves by construction -- every unit a pinned locus gives up goes back
 * into the potential the remaining pool divides. Because each node's ceiling is
 * at least its current value, the component's total is always placeable, so the
 * pool never empties with charge left over.
 *
 * **A component with no capacity anywhere is left alone.** `SumC` of 0 would be
 * a division by zero, and the honest reading is that there is nowhere in this
 * component for charge to be -- a wire joined only to wires stores nothing, so
 * there is nothing to solve. Whatever sits on those loci stays put rather than
 * being redistributed to nowhere (which would delete it) or spread evenly
 * (which is the old equal-share assumption sneaking back in). */
function equalizedValues(component: ChargeNode[]): Map<string, number> {
  const settled = new Map<string, number>();
  let pool = [...component];
  let remaining = component.reduce((sum, node) => sum + node.get(), 0);

  // No store anywhere in the component -- see networkPotential's own note. Each
  // locus counts as its own unit, which is precisely the equal-share rule this
  // generalizes, kept for the one case the capacity model does not describe.
  // Applied to the *assignment* as well as the potential, or the two would
  // disagree and a capacity-0 locus would be handed 0 out of a mean it helped
  // compute.
  //
  // **The caster does not count toward this question** (fixed 2026-08-08, from a
  // bug report: a charged caster stepping onto room15's unfed Metal Floor strip
  // was not shocked). Whether a network has a store of its own is a property of
  // the network, not of who is standing on it -- and the caster's capacity of 1
  // is enough to flip a bare grid out of this branch, at which point its tiles
  // stop being unit-capacity bodies and become non-entities that can hold
  // nothing. The result was an observer-dependent network: a bare grid's tiles
  // counted as units when nobody touched them and as nothing the instant someone
  // did, so a charged caster could never discharge into one however live it read.
  // That silently confined the symmetric half of the floor hazard to networks
  // that happened to have a store on them -- room11 (Fonts) yes, room15 no.
  const degenerate = component.filter((node) => !node.isCaster).reduce((sum, node) => sum + node.capacity, 0) === 0;
  const capacityOf = (node: ChargeNode) => (degenerate ? 1 : node.capacity);

  while (pool.length > 0) {
    const totalCapacity = pool.reduce((sum, node) => sum + capacityOf(node), 0);
    // Only capacity-0 loci left: none can hold the remainder, and whatever
    // they were carrying has already gone to the capacity-bearing loci pinned
    // in earlier rounds.
    if (totalCapacity === 0) {
      for (const node of pool) settled.set(node.key, 0);
      break;
    }
    const potential = remaining / totalCapacity;
    const pinned = pool.filter((node) => node.ceiling < capacityOf(node) * potential);
    if (pinned.length === 0) {
      for (const node of pool) settled.set(node.key, capacityOf(node) * potential);
      break;
    }
    for (const node of pinned) {
      settled.set(node.key, node.ceiling);
      remaining -= node.ceiling;
    }
    pool = pool.filter((node) => !pinned.includes(node));
  }
  return settled;
}

/** Equalizes every Bind·Charge component in the player's current room, and
 * bills the caster for whatever crossed.
 *
 * Resistive loss is charged on the total Charge that actually moved -- the sum
 * of the positive deltas, which is exactly the amount that left one locus and
 * arrived at another, counted once rather than once per endpoint. It lands on
 * the caster's own `thermalState` because with nothing between the two loci
 * but the binding, the caster *is* the conductor (see `ChargeBindDef`), and it
 * goes through `giveThermal` so it picks up the same ceiling clamp and the
 * same two-sided danger check every other route into that locus does.
 *
 * Runs as a settle system rather than on the room-decay tick, and is ordered
 * before `conduit-gates`, so a cast that charges either end of a wire opens
 * the gate at the far end within the same resolution. */
export function settleChargeBind(world: WorldState, out: SimEvents): void {
  const room = world.rooms[world.player.roomId];
  if (!room) return;

  let moved = 0;
  for (const component of chargeComponents(world, room)) {
    const settled = equalizedValues(component);
    for (const node of component) {
      // A capacity-less component settles nothing (see equalizedValues), so a
      // node can legitimately be absent here. Reading through it would write
      // `undefined` into properties.charge and turn every later sum into NaN,
      // which is a far worse failure than the no-op it stands in for.
      const value = settled.get(node.key);
      if (value === undefined) continue;
      const delta = value - node.get();
      if (delta > 0) moved += delta;
      node.set(value);
    }
  }

  if (moved > 0) giveThermal(world, moved * world.physics.bind.jouleHeatPerCharge, out);
}

/** The caster touching a conductor: equalize them into its network, and report
 * the signed Charge that crossed (positive into the caster).
 *
 * This is the whole of the 2026-08-08 floor hazard. Contact conducts -- there
 * is nothing special about a foot on a plate that a wire does not already do --
 * so this is the *same* solver `settleChargeBind` uses, over the same network
 * `isConduitLive` reads, with the caster appended as one more locus. What makes
 * it a hazard rather than a convenience is only that the caller bills Health
 * for the charge that moved (see `applyFloorHazard`).
 *
 * Three behaviours are consequences of the arithmetic rather than rules:
 *
 * - **It self-limits.** Once the caster sits at the network's potential there
 *   is nothing left to cross, so standing there, or stepping back on, costs
 *   nothing -- which preserves `applyFloorHazard`'s original "crossing onto it
 *   is the danger, not lingering" intent instead of contradicting it. What
 *   re-arms a sourced network is relaxation (`relaxChargeNetworks`); a
 *   sourceless one, having been drained into the caster, stays safe.
 * - **It is symmetric.** A charged caster stepping onto a neutral network dumps
 *   into it and is billed for the same crossing, because `equalizedValues` has
 *   no idea which side it is solving for.
 * - **A store-less network shares evenly, in both directions.** A bare floor
 *   grid has no capacity of its own, so `equalizedValues`' degeneracy branch
 *   treats every locus -- its tiles and the caster alike -- as one unit: a
 *   charged grid gives up a share to whoever steps on it, and a charged caster
 *   gives up a share to it. This read the other way until a room15 bug report
 *   (2026-08-08): the caster's own capacity of 1 used to flip a bare grid out of
 *   that branch, turning its tiles into non-entities that could hold nothing, so
 *   a charged caster could never discharge into one however live it read.
 *
 * No Joule tax here, unlike `settleChargeBind`: a wire has the caster acting as
 * the conductor between two other loci and bills them for it, whereas here the
 * caster is an *endpoint*. The Health is the cost of this crossing; charging
 * thermalState on top would be billing the same event twice. */
export function conductFloorContact(world: WorldState, tile: ObjectDef): number {
  const room = world.rooms[world.player.roomId];
  if (!room) return 0;

  const loci = chargeNetwork(room, tile, world.physics, { charge: world.player.charge });
  // Already a member (wired in with Bind·Charge): `settleChargeBind` has
  // equalized them into this network already, so there is no gradient to
  // resolve and appending a second copy of the caster would double their
  // capacity. `isConduitLive` reads false in this case for the same reason, so
  // this is belt-and-braces -- but it is what makes this function honest to
  // call on its own.
  if (loci.some((l) => l.kind === 'caster')) return 0;

  const members = loci.filter((l) => l.kind === 'object' && l.obj.properties?.charge !== undefined);
  const nodes = [playerNode(world), ...members.map((l) => objectNode((l as { kind: 'object'; obj: ObjectDef }).obj))];

  const before = world.player.charge;
  const settled = equalizedValues(nodes);
  for (const node of nodes) {
    const value = settled.get(node.key);
    if (value === undefined) continue; // see settleChargeBind's own note
    node.set(value);
  }
  return world.player.charge - before;
}

/** Every charge network in the room that contains at least one *source* -- an
 * object declaring `maxProperties.charge` -- paired with that network's
 * equilibrium, the sum of those sources' rated output.
 *
 * Sourceless networks are deliberately absent. With no generator there is
 * nothing to restore anything, and an isolated charged conductor really does
 * just stay charged; relaxing one toward zero would be charge quietly
 * evaporating, which is a different and unphysical claim. It is also what keeps
 * room8's hand-charged Standing Pillars working -- they sit on no source's
 * network, so nothing pulls them anywhere. */
function sourcedNetworks(world: WorldState, room: RoomDef): { nodes: ChargeNode[]; equilibrium: number }[] {
  const seen = new Set<string>();
  const out: { nodes: ChargeNode[]; equilibrium: number }[] = [];
  for (const seed of room.objects) {
    if (seed.maxProperties?.charge === undefined || seen.has(seed.id)) continue;
    const loci = chargeNetwork(room, seed, world.physics, { charge: world.player.charge });
    const members = loci.filter((l) => l.kind === 'caster' || l.obj.properties?.charge !== undefined);
    let equilibrium = 0;
    for (const l of members) {
      if (l.kind === 'caster') continue;
      seen.add(l.obj.id);
      equilibrium += l.obj.maxProperties?.charge ?? 0;
    }
    if (equilibrium > 0) {
      out.push({
        nodes: members.map((l) => (l.kind === 'caster' ? playerNode(world) : objectNode(l.obj))),
        equilibrium,
      });
    }
  }
  return out;
}

/** One tick's worth of a charge network returning to its sources' level.
 *
 * The half of regen that was missing. `applyRoomDecay` used to pull each
 * source's own `properties.charge` back up toward its own `maxProperties.charge`
 * and nothing ever pulled injected Charge back *down*, so a network could be
 * topped up and banked indefinitely -- Phase 7's pump. Here the whole network's
 * total moves `physics.charge.relaxPerTick` units toward its equilibrium in
 * whichever direction it is on -- stopping exactly there rather than
 * asymptoting, which is what makes "this network holds its charge for three
 * moves" a sentence the world can express (see physics.ts's two approach
 * shapes), and the result is redistributed by capacity
 * through the same solver a wire uses, so a relaxing network stays internally
 * consistent rather than having two rules fighting over the same loci.
 *
 * Called from `tick()` alongside `applyRoomDecay`, and -- unlike the rest of
 * decay -- it genuinely does invalidate a system: `conduit-gates` reads exactly
 * the Charge this moves. `tick()` settles afterwards for that reason. */
export function relaxChargeNetworks(world: WorldState, out: SimEvents): boolean {
  const room = world.rooms[world.player.roomId];
  if (!room) return false;
  const perTick = world.physics.charge.relaxPerTick;

  let moved = 0;
  let changed = false;
  for (const { nodes, equilibrium } of sourcedNetworks(world, room)) {
    const total = nodes.reduce((sum, node) => sum + node.get(), 0);
    const target = total + countedDelta(equilibrium - total, perTick);
    if (Math.abs(target - total) < 1e-9) continue;
    changed = true;

    // Redistribute the new total through the same capacity solver a wire uses.
    // A scratch node list carrying the post-relaxation total is the cheapest
    // honest way to say "the network now holds `target`, share it out the
    // normal way" without duplicating the water-filling.
    const scaled = total === 0 ? undefined : target / total;
    const settled = equalizedValues(
      nodes.map((node) => ({ ...node, get: () => (scaled === undefined ? target / nodes.length : node.get() * scaled) })),
    );
    for (const node of nodes) {
      const value = settled.get(node.key);
      if (value === undefined) continue;
      const delta = value - node.get();
      if (delta > 0) moved += delta;
      node.set(value);
    }
  }
  if (moved > 0) out.push({ kind: 'stats-changed' });
  return changed;
}
